import { removeLocalStorage } from 'helpers/storage';
import {
  all, call, delay, put, select, takeLatest,
} from 'redux-saga/effects';
import * as constants from 'redux/constants/cart.constants';
import { delivery, pickup, submitCoupon } from 'services/cart.services';
import { syncPendingCartSnapshots } from 'services/abandonedCart.services';
import {
  prepareCartSnapshot,
  removeCartSnapshot,
} from 'helpers/cartSnapshotOutbox';
import { getCartStoreKey, getCartToken, rotateCartToken } from 'helpers/cartScope';
import { getIds } from 'services/product.services';
import { cartSubtotalSelector, cartSubtotalTextilSelector } from 'redux/selectors/cart.selector';
import { buildCheckoutSummary, buildWhatsAppOrderMessage, resolveWhatsappNumber } from 'helpers/whatsappOrderMessage';

const getPaymentMethodLabel = (formData, paymentMethods = []) => {
  const code = formData?.payment_method;
  if (!code) return '';
  const method = paymentMethods.find((item) => item.code === code);
  return method?.name || code;
};

const emailFromCheckout = (formData = {}) => formData.email || formData.email_retire || null;
const phoneFromCheckout = (formData = {}) => formData.phone || formData.phone_retire || null;

function* flushCheckoutSnapshot(action) {
  const state = yield select();
  try {
    yield call(prepareCartSnapshot, {
      items: action.items || state.cart.items,
      itemsSub: action.itemsSub || state.cart.itemsSub,
      products: state.cart.products,
      customerEmail: emailFromCheckout(action.formData),
      customerPhone: phoneFromCheckout(action.formData),
    });
    yield call(syncPendingCartSnapshots);
  } catch (error) {
    // Cart capture must never prevent a customer from completing checkout.
  }
}

function* clearCompletedCartSnapshot() {
  const storeKey = getCartStoreKey();
  const cartToken = getCartToken();
  try {
    if (cartToken) yield call(removeCartSnapshot, storeKey, cartToken);
  } finally {
    rotateCartToken();
  }
}

function* retryPendingCartSnapshots() {
  const maxAttempts = 3;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      yield call(syncPendingCartSnapshots);
      return;
    } catch (error) {
      if (attempt === maxAttempts - 1) {
        // Keep the outbox pending; the app retries again on the next cart change,
        // browser reconnect, or storefront startup.
        console.warn('No se pudo sincronizar el carrito abandonado; queda pendiente para reintentar.', error?.message || error);
        return;
      }
      const backoffMs = (750 * (2 ** attempt)) + Math.floor(Math.random() * 500);
      yield delay(backoffMs);
    }
  }
}

export function* cartSnapshotSync(action) {
  try {
    const cart = yield select((state) => state.cart);
    const snapshot = yield call(prepareCartSnapshot, {
      items: action.items ?? cart.items,
      itemsSub: action.itemsSub ?? cart.itemsSub,
      products: action.products || cart.products,
      customerEmail: action.customerEmail,
      customerPhone: action.customerPhone,
    });
    if (!snapshot || snapshot.status !== 'pending') return;

    // Sync contact details before the customer can leave checkout.
    if (action.customerEmail || action.customerPhone) {
      yield call(retryPendingCartSnapshots);
      return;
    }

    // Agrupa cambios rápidos y reintenta errores transitorios sin frenar el checkout.
    yield delay(3000 + Math.floor(Math.random() * 3000));
    yield call(retryPendingCartSnapshots);
  } catch (error) {
    console.warn('No se pudo preparar el snapshot del carrito abandonado.', error?.message || error);
  }
}

export function* cartSnapshotSyncPending() {
  yield call(retryPendingCartSnapshots);
}

export function* cartSnapshotClear(action) {
  if (!action.storeKey || !action.cartToken) return;
  yield call(removeCartSnapshot, action.storeKey, action.cartToken);
}

function* buildSummaryFromAction(action, checkoutType) {
  const state = yield select();
  const client = state.app.client;
  const subtotal = client?.design === 3
    ? cartSubtotalTextilSelector(state)
    : cartSubtotalSelector(state);

  return buildCheckoutSummary({
    items: action.items,
    products: state.cart.products,
    formData: action.formData,
    checkoutType,
    subtotal,
    shippingCost: action.formData?.shipping_cost ?? 0,
    coupon: state.cart.coupon,
    clientName: client?.name_show || client?.name,
    paymentMethod: getPaymentMethodLabel(action.formData, state.app.paymentMethods),
  });
}

export function* cartCheckoutDelivery(action) {
  try {
    yield call(flushCheckoutSnapshot, action);
    const checkoutSummary = yield buildSummaryFromAction(action, 'delivery');
    const data = yield call(delivery, action);
    const state = yield select();
    const whatsappNumber = resolveWhatsappNumber({
      whatsapp: state.app.whatsapp,
      client: state.app.client,
    });
    const whatsappMessage = buildWhatsAppOrderMessage(checkoutSummary, data.orderId);
    yield put({
      type: constants.CART_CHECKOUT_SUCCEEDED,
      data: {
        ...data, checkoutSummary, whatsappMessage, whatsappNumber,
      },
    });
    yield call(clearCompletedCartSnapshot);
    removeLocalStorage('descriptionsCart');
  } catch (error) {
    yield put({ type: constants.CART_CHECKOUT_FAILED, error });
  }
}

export function* cartCheckoutPickup(action) {
  try {
    yield call(flushCheckoutSnapshot, action);
    const checkoutSummary = yield buildSummaryFromAction(action, 'pickup');
    const data = yield call(pickup, action);
    const state = yield select();
    const whatsappNumber = resolveWhatsappNumber({
      whatsapp: state.app.whatsapp,
      client: state.app.client,
    });
    const whatsappMessage = buildWhatsAppOrderMessage(checkoutSummary, data.orderId);
    yield put({
      type: constants.CART_CHECKOUT_SUCCEEDED,
      data: {
        ...data, checkoutSummary, whatsappMessage, whatsappNumber,
      },
    });
    yield call(clearCompletedCartSnapshot);
    removeLocalStorage('descriptionsCart');
  } catch (error) {
    yield put({ type: constants.CART_CHECKOUT_FAILED, error });
  }
}

export function* cartGetProducts(action) {
  try {
    if (action.value.length > 0) {
      const data = yield call(getIds, action.value);
      yield put({ type: constants.CART_GET_PRODUCTS_SUCCEEDED, data });
    }
  } catch (error) {
    yield put({ type: constants.CART_GET_PRODUCTS_FAILED, error });
  }
}

export function* cartSubmitCoupon(action) {
  try {
    const data = yield call(submitCoupon, action.coupon, action.email);
    yield put({ type: constants.CART_COUPON_SUCCEEDED, data });
  } catch (error) {
    yield put({ type: constants.CART_COUPON_FAILED, error });
  }
}

export function* watchCart() {
  yield all([
    takeLatest(constants.CART_SNAPSHOT_SYNC_REQUESTED, cartSnapshotSync),
    takeLatest(constants.CART_SNAPSHOT_SYNC_PENDING, cartSnapshotSyncPending),
    takeLatest(constants.CART_SNAPSHOT_CLEAR_REQUESTED, cartSnapshotClear),
    takeLatest(constants.CART_CHECKOUT_DELIVERY_REQUESTED, cartCheckoutDelivery),
    takeLatest(constants.CART_CHECKOUT_PICKUP_REQUESTED, cartCheckoutPickup),
    takeLatest(constants.CART_GET_PRODUCTS_REQUESTED, cartGetProducts),
    takeLatest(constants.CART_COUPON_REQUESTED, cartSubmitCoupon),
  ]);
}
